/*
 * FIND YOUR FATE - Printable Character Dossier v4
 * Dice Dungeons
 *
 * Install as assets/find-your-fate-print.js.
 * Uses the existing result markup and selected dice product cards.
 * No changes to the generator, Liquid section, or product picker required.
 */
(function () {
  'use strict';

  const SHEET_ID = 'fyf-print-root';
  const STYLE_ID = 'fyf-print-editorial-styles';
  const HOME_URL = 'https://dicedungeons.com/pages/find-your-fate';
  const classHandles = {
    Barbarian: 'class-barbarian', Bard: 'class-bard',
    Cleric: 'class-cleric', Druid: 'class-druid',
    Fighter: 'class-fighter', Monk: 'class-monk',
    Paladin: 'class-paladin', Ranger: 'class-ranger',
    Rogue: 'class-rogue', Sorcerer: 'class-sorcerer',
    Warlock: 'class-warlock', Wizard: 'class-wizard'
  };

  let previouslyFocused = null;
  let priorOverflow = '';

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function getText(root, selector) {
    const node = root.querySelector(selector);
    return node ? node.textContent.trim() : '';
  }

  function addField(parent, heading, text, kind) {
    if (!text) return;
    const field = el('section', 'fyfp-field' + (kind ? ' ' + kind : ''));
    field.appendChild(el('h2', 'fyfp-field-title', heading));
    field.appendChild(el('p', 'fyfp-field-text', text));
    parent.appendChild(field);
  }

  function getDice(root) {
    return Array.from(root.querySelectorAll(
      '[data-fyf-dice-grid] .fyf-product-card'
    )).slice(0, 3).map(card => {
      const image = card.querySelector('img');
      const select = card.querySelector('select');
      const link = card.querySelector('a[href]');
      const rawVariant = select && select.selectedOptions.length
        ? select.selectedOptions[0].textContent.trim() : '';
      // Keep the variant name, but don't repeat its price on the sheet.
      const variant = rawVariant
        .replace(/\s+-\s+\$[\d,.]+(?:\s+[A-Z]{3})?\s*$/, '')
        .replace(/^default title$/i, '')
        .trim();
      return {
        name: getText(card, 'h4'),
        label: getText(card, '.fyf-product-label'),
        price: getText(card, '.fyf-product-price'),
        variant,
        image: image ? (image.currentSrc || image.src) : '',
        url: link ? link.href : ''
      };
    });
  }

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    const style = el('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${SHEET_ID}, #${SHEET_ID} * { box-sizing: border-box; }
      #${SHEET_ID} {
        --fyfp-ink: #2e2930;
        --fyfp-plum: #45354a;
        --fyfp-gold: #997747;
        --fyfp-muted: #716c70;
        --fyfp-rule: #d9d2c8;
        position: fixed; inset: 0; z-index: 2147483646;
        overflow-y: auto; padding: 0 12px 36px;
        background: #eae7e2; color: var(--fyfp-ink);
        font-family: Arial, Helvetica, sans-serif;
      }
      #${SHEET_ID} .fyfp-toolbar {
        max-width: 820px; margin: 0 auto 13px; padding: 12px 0;
        display: flex; align-items: center; justify-content: space-between;
        gap: 10px; position: sticky; top: 0; z-index: 2;
        background: #eae7e2;
      }
      #${SHEET_ID} .fyfp-toolbar-label { font-size: 14px; font-weight: 700; }
      #${SHEET_ID} .fyfp-toolbar-actions { display: flex; gap: 8px; }
      #${SHEET_ID} button {
        font: inherit; font-size: 14px; font-weight: 700; cursor: pointer;
        padding: 11px 14px; border-radius: 5px;
      }
      #${SHEET_ID} .fyfp-print-btn { background: #45354a; color: white; border: 1px solid #45354a; }
      #${SHEET_ID} .fyfp-close-btn { background: white; color: #45354a; border: 1px solid #aaa0a4; }
      #${SHEET_ID} button:focus-visible { outline: 3px solid #997747; outline-offset: 2px; }
      #${SHEET_ID} .fyfp-page {
        width: 100%; max-width: 820px; margin: 0 auto;
        padding: 35px 43px 26px; background: #fff;
        border: 1px solid #d8d2c9;
        box-shadow: 0 12px 34px rgba(31,25,26,.12);
      }
      #${SHEET_ID} .fyfp-masthead {
        display: flex; justify-content: space-between; align-items: baseline;
        gap: 16px; padding-bottom: 10px;
        border-bottom: 2px solid var(--fyfp-plum);
        font-size: 10px; letter-spacing: .16em; font-weight: 800;
        text-transform: uppercase; color: var(--fyfp-plum);
      }
      #${SHEET_ID} .fyfp-masthead .fyfp-series { color: var(--fyfp-muted); text-align: right; }
      #${SHEET_ID} .fyfp-heading {
        text-align: center; padding: 23px 0 23px;
        border-bottom: 1px solid var(--fyfp-rule);
      }
      #${SHEET_ID} .fyfp-kicker {
        color: var(--fyfp-gold); font-size: 10px; letter-spacing: .22em;
        font-weight: 800; text-transform: uppercase; margin-bottom: 8px;
      }
      #${SHEET_ID} .fyfp-heading h1 {
        font: 500 clamp(29px, 4.3vw, 39px)/1.12 Georgia, 'Times New Roman', serif;
        color: var(--fyfp-plum); overflow-wrap: anywhere;
        letter-spacing: -.025em; margin: 0 0 10px;
      }
      #${SHEET_ID} .fyfp-subline {
        display: flex; justify-content: center; align-items: center;
        gap: 11px; flex-wrap: wrap;
      }
      #${SHEET_ID} .fyfp-type {
        color: var(--fyfp-ink); font-size: 13px;
        font-weight: 800; margin: 0;
      }
      #${SHEET_ID} .fyfp-separator { width: 20px; height: 1px; background: var(--fyfp-gold); }
      #${SHEET_ID} .fyfp-mode { color: var(--fyfp-muted); font-size: 12px; margin: 0; }
      #${SHEET_ID} .fyfp-main { padding-top: 20px; }
      #${SHEET_ID} .fyfp-story {
        border-left: 3px solid var(--fyfp-gold);
        padding: 2px 0 3px 19px; margin-bottom: 20px;
      }
      #${SHEET_ID} .fyfp-field-title {
        color: var(--fyfp-plum); font-family: Georgia, 'Times New Roman', serif;
        font-size: 18px; font-weight: 700; line-height: 1.2;
        margin: 0 0 7px;
      }
      #${SHEET_ID} .fyfp-field-text {
        color: var(--fyfp-ink); font-size: 12.5px;
        line-height: 1.55; margin: 0;
      }
      #${SHEET_ID} .fyfp-story .fyfp-field-title { font-size: 21px; }
      #${SHEET_ID} .fyfp-story .fyfp-field-text {
        font: 14px/1.65 Georgia, 'Times New Roman', serif;
      }
      #${SHEET_ID} .fyfp-details {
        display: grid; grid-template-columns: 1fr 1fr;
        border-top: 1px solid var(--fyfp-rule);
        border-bottom: 1px solid var(--fyfp-rule);
        padding: 15px 0 16px;
      }
      #${SHEET_ID} .fyfp-details .fyfp-field { padding-right: 19px; }
      #${SHEET_ID} .fyfp-details .fyfp-field + .fyfp-field {
        padding-left: 19px; padding-right: 0;
        border-left: 1px solid var(--fyfp-rule);
      }
      #${SHEET_ID} .fyfp-details .fyfp-field-title { font-size: 16px; margin-bottom: 5px; }
      #${SHEET_ID} .fyfp-rumor {
        padding: 15px 0 16px; border-bottom: 1px solid var(--fyfp-rule);
        margin: 0;
      }
      #${SHEET_ID} .fyfp-rumor .fyfp-field-title { font-size: 16px; margin-bottom: 6px; }
      #${SHEET_ID} .fyfp-rumor .fyfp-field-text {
        font: italic 14px/1.45 Georgia, 'Times New Roman', serif;
      }
      /* Space for the player, ahead of the optional product picks. */
      #${SHEET_ID} .fyfp-notes { padding: 18px 0 0; }
      #${SHEET_ID} .fyfp-notes-title {
        color: var(--fyfp-plum); font: 700 18px Georgia, 'Times New Roman', serif;
        margin: 0 0 10px;
      }
      #${SHEET_ID} .fyfp-note-lines { display: grid; grid-template-rows: repeat(6, 29px); }
      #${SHEET_ID} .fyfp-note-line { border-bottom: 1px solid var(--fyfp-rule); }
      /* Recommendations are a modest footer detail, not the main feature. */
      #${SHEET_ID} .fyfp-shop {
        padding-top: 16px; margin-top: 17px;
        border-top: 1px solid var(--fyfp-rule);
      }
      #${SHEET_ID} .fyfp-shop-head {
        display: flex; gap: 12px; align-items: baseline;
        justify-content: space-between; margin-bottom: 11px;
      }
      #${SHEET_ID} .fyfp-shop-title {
        color: var(--fyfp-plum); font: 700 14px Georgia, 'Times New Roman', serif;
        margin: 0;
      }
      #${SHEET_ID} .fyfp-shop-caption {
        color: var(--fyfp-muted); font-size: 9px; text-align: right; margin: 0;
      }
      #${SHEET_ID} .fyfp-dice-grid {
        display: grid; grid-template-columns: repeat(3, minmax(0,1fr));
        gap: 12px;
      }
      #${SHEET_ID} .fyfp-dice-card {
        min-width: 0; display: grid;
        grid-template-columns: 43px minmax(0,1fr);
        gap: 8px; align-items: start; text-align: left;
      }
      #${SHEET_ID} .fyfp-dice-photo {
        display: flex; align-items: center; justify-content: center;
        width: 43px; height: 43px;
      }
      #${SHEET_ID} .fyfp-dice-photo img {
        display: block; width: 100%; height: 100%; object-fit: contain;
      }
      #${SHEET_ID} .fyfp-dice-info { min-width: 0; }
      #${SHEET_ID} .fyfp-dice-label {
        display: block; text-transform: uppercase; font-size: 7.5px;
        font-weight: 700; letter-spacing: .06em; color: var(--fyfp-muted);
      }
      #${SHEET_ID} .fyfp-dice-name {
        display: block; color: var(--fyfp-plum); text-decoration: none;
        font-size: 9.5px; line-height: 1.28; font-weight: 700;
        overflow-wrap: anywhere; margin-top: 3px;
      }
      #${SHEET_ID} .fyfp-dice-name:hover { text-decoration: underline; }
      #${SHEET_ID} .fyfp-variant {
        font-size: 8px; line-height: 1.25;
        color: var(--fyfp-muted); margin-top: 2px;
      }
      #${SHEET_ID} .fyfp-dice-price {
        font-size: 9px; font-weight: 700;
        color: var(--fyfp-ink); padding-top: 3px;
      }
      #${SHEET_ID} .fyfp-footer {
        display: flex; justify-content: space-between; align-items: center;
        gap: 18px; border-top: 2px solid var(--fyfp-plum);
        margin-top: 22px; padding-top: 11px;
        font-size: 10px; color: var(--fyfp-muted);
      }
      #${SHEET_ID} .fyfp-footer-brand { font-weight: 800; letter-spacing: .04em; }
      #${SHEET_ID} .fyfp-footer-links { text-align: right; }
      #${SHEET_ID} .fyfp-footer a {
        color: var(--fyfp-plum); text-decoration: underline;
        text-underline-offset: 2px;
      }
      @media screen and (max-width: 560px) {
        #${SHEET_ID} { padding-inline: 8px; }
        #${SHEET_ID} .fyfp-toolbar { flex-direction: column; align-items: stretch; }
        #${SHEET_ID} .fyfp-toolbar-actions button { flex: 1; }
        #${SHEET_ID} .fyfp-page { padding: 22px 16px; }
        #${SHEET_ID} .fyfp-heading { padding: 17px 0; }
        #${SHEET_ID} .fyfp-heading h1 { font-size: 29px; }
        #${SHEET_ID} .fyfp-shop-head { display: block; }
        #${SHEET_ID} .fyfp-shop-caption { text-align: left; margin-top: 3px; }
        #${SHEET_ID} .fyfp-details .fyfp-field { padding-right: 10px; }
        #${SHEET_ID} .fyfp-details .fyfp-field + .fyfp-field { padding-left: 10px; }
        #${SHEET_ID} .fyfp-dice-grid { gap: 7px; }
        #${SHEET_ID} .fyfp-dice-card { display: block; }
        #${SHEET_ID} .fyfp-dice-photo { width: 32px; height: 32px; margin-bottom: 4px; }
        #${SHEET_ID} .fyfp-dice-name { font-size: 9px; }
        #${SHEET_ID} .fyfp-footer { display: block; }
        #${SHEET_ID} .fyfp-footer-links { text-align: left; margin-top: 6px; }
      }
      @page { size: letter; margin: .42in; }
      @media print {
        html, body { background: white !important; height: auto !important;
          overflow: visible !important; padding: 0 !important; margin: 0 !important; }
        body > :not(#${SHEET_ID}) { display: none !important; }
        #${SHEET_ID} { position: static !important; inset: auto !important;
          overflow: visible !important; padding: 0 !important; margin: 0 !important;
          background: white !important; }
        #${SHEET_ID} .fyfp-toolbar { display: none !important; }
        #${SHEET_ID} .fyfp-page {
          width: 100% !important; max-width: none !important;
          margin: 0 !important; padding: 0 !important;
          border: none !important; box-shadow: none !important;
        }
        #${SHEET_ID} .fyfp-heading { padding-top: 19px; padding-bottom: 17px; }
        #${SHEET_ID} .fyfp-main { padding-top: 17px; }
        #${SHEET_ID} .fyfp-story { margin-bottom: 16px; }
        #${SHEET_ID} .fyfp-notes { padding-top: 14px; }
        #${SHEET_ID} .fyfp-note-lines { grid-template-rows: repeat(6, 26px); }
        #${SHEET_ID} .fyfp-shop { padding-top: 12px; margin-top: 13px; }
        #${SHEET_ID} .fyfp-footer { margin-top: 13px; }
        #${SHEET_ID} .fyfp-story, #${SHEET_ID} .fyfp-details,
        #${SHEET_ID} .fyfp-rumor, #${SHEET_ID} .fyfp-notes,
        #${SHEET_ID} .fyfp-shop, #${SHEET_ID} .fyfp-dice-card,
        #${SHEET_ID} .fyfp-footer {
          break-inside: avoid; page-break-inside: avoid;
        }
        #${SHEET_ID}, #${SHEET_ID} * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `;
    document.head.appendChild(style);
  }

  function closeSheet() {
    const overlay = document.getElementById(SHEET_ID);
    if (!overlay) return;
    overlay.remove();
    document.body.style.overflow = priorOverflow;
    if (previouslyFocused && document.contains(previouslyFocused)) {
      previouslyFocused.focus();
    }
  }

  function openSheet(root) {
    const result = root.querySelector('[data-fyf-result]');
    if (!result || result.hidden) return;

    const dice = getDice(root);
    const status = getText(root, '[data-fyf-status]');
    if (!dice.length && /finding.*dice/i.test(status)) {
      window.alert('Your dice recommendations are still loading. Please try again in a moment.');
      return;
    }

    closeSheet();
    ensureStyles();
    previouslyFocused = document.activeElement;
    priorOverflow = document.body.style.overflow;

    const overlay = el('div');
    overlay.id = SHEET_ID;
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Printable character sheet');

    const toolbar = el('div', 'fyfp-toolbar');
    toolbar.appendChild(el('span', 'fyfp-toolbar-label', 'Character sheet preview'));
    const actions = el('div', 'fyfp-toolbar-actions');
    const printBtn = el('button', 'fyfp-print-btn', 'Print / Save PDF');
    printBtn.type = 'button';
    printBtn.addEventListener('click', function () { window.print(); });
    const closeBtn = el('button', 'fyfp-close-btn', 'Close');
    closeBtn.type = 'button';
    closeBtn.addEventListener('click', closeSheet);
    actions.appendChild(printBtn);
    actions.appendChild(closeBtn);
    toolbar.appendChild(actions);
    overlay.appendChild(toolbar);

    const sheet = el('main', 'fyfp-page');
    const masthead = el('div', 'fyfp-masthead');
    masthead.appendChild(el('span', '', 'Dice Dungeons'));
    masthead.appendChild(el('span', 'fyfp-series', 'Find Your Fate  /  Character Dossier'));
    sheet.appendChild(masthead);

    const head = el('header', 'fyfp-heading');
    head.appendChild(el('div', 'fyfp-kicker', 'Your Next Adventure Begins Here'));
    head.appendChild(el('h1', '', getText(root, '[data-fyf-name]') || 'Your Adventurer'));
    const subline = el('div', 'fyfp-subline');
    subline.appendChild(el('p', 'fyfp-type', getText(root, '[data-fyf-type]')));
    subline.appendChild(el('span', 'fyfp-separator'));
    subline.appendChild(el('p', 'fyfp-mode', getText(root, '[data-fyf-mode-label]')));
    head.appendChild(subline);
    sheet.appendChild(head);

    const main = el('div', 'fyfp-main');
    addField(main, getText(root, '[data-fyf-story-label]') || 'Your Story',
      getText(root, '[data-fyf-story]'), 'fyfp-story');

    const details = el('div', 'fyfp-details');
    addField(details, 'Personality', getText(root, '[data-fyf-personality]'));
    addField(details, 'Character Quirk', getText(root, '[data-fyf-quirk]'));
    main.appendChild(details);

    addField(main, getText(root, '[data-fyf-note-label]') || 'A Rumor About You',
      getText(root, '[data-fyf-calling]'), 'fyfp-rumor');

    // Six ruled lines intentionally left blank for handwritten character notes.
    const notes = el('section', 'fyfp-notes');
    notes.appendChild(el('h2', 'fyfp-notes-title', "Adventurer's Notes"));
    const noteLines = el('div', 'fyfp-note-lines');
    for (let i = 0; i < 6; i++) {
      noteLines.appendChild(el('div', 'fyfp-note-line'));
    }
    notes.appendChild(noteLines);
    main.appendChild(notes);
    sheet.appendChild(main);

    const shop = el('section', 'fyfp-shop');
    const shopHead = el('div', 'fyfp-shop-head');
    shopHead.appendChild(el('h2', 'fyfp-shop-title', 'Suggested Dice'));
    shopHead.appendChild(el('p', 'fyfp-shop-caption', 'Optional picks for this character'));
    shop.appendChild(shopHead);

    const diceGrid = el('div', 'fyfp-dice-grid');
    if (!dice.length) {
      diceGrid.appendChild(el('p', '', 'No dice recommendations available yet.'));
    }
    dice.forEach(item => {
      const card = el('div', 'fyfp-dice-card');
      const photo = el('div', 'fyfp-dice-photo');
      if (item.image) {
        const image = el('img');
        image.src = item.image;
        image.alt = item.name;
        image.loading = 'eager';
        photo.appendChild(image);
      }
      card.appendChild(photo);
      const info = el('div', 'fyfp-dice-info');
      info.appendChild(el('div', 'fyfp-dice-label', item.label));
      const name = el(item.url ? 'a' : 'div', 'fyfp-dice-name', item.name);
      if (item.url) name.href = item.url;
      info.appendChild(name);
      if (item.variant) info.appendChild(el('div', 'fyfp-variant', item.variant));
      info.appendChild(el('div', 'fyfp-dice-price', item.price));
      card.appendChild(info);
      diceGrid.appendChild(card);
    });
    shop.appendChild(diceGrid);
    sheet.appendChild(shop);

    const footer = el('footer', 'fyfp-footer');
    footer.appendChild(el('div', 'fyfp-footer-brand', 'DICEDUNGEONS.COM'));
    const links = el('div', 'fyfp-footer-links');
    const homeLink = el('a', '', 'Find Your Fate');
    homeLink.href = HOME_URL;
    links.appendChild(homeLink);
    const className = getText(root, '[data-fyf-type]').trim().split(/\s+/).pop();
    if (classHandles[className]) {
      links.appendChild(el('span', '', '   |   '));
      const classLink = el('a', '', 'More ' + className + ' Dice');
      classLink.href = 'https://dicedungeons.com/collections/' + classHandles[className];
      links.appendChild(classLink);
    }
    footer.appendChild(links);
    sheet.appendChild(footer);
    overlay.appendChild(sheet);

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    printBtn.focus();
  }

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-fyf-print]');
    if (!button) return;
    event.preventDefault();
    const root = button.closest('.fyf');
    if (root) openSheet(root);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && document.getElementById(SHEET_ID)) {
      closeSheet();
    }
  });
})();
